// pages/ReportsDocumentPage.tsx
import {
  BarChartOutlined,
  DollarOutlined,
  FileExcelOutlined,
  UserOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Segmented,
  Space,
  Typography,
  message,
} from "antd";
import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import { useQuery } from "react-query";
import CustomerReport from "../components/reports/CustomerReport";
import CourseReport from "../components/reports/CourseReport";
import ActiveCoursesReport from "../components/reports/ActiveCoursesReport";
import type { ReportParams } from "../services/customerReportService";
import { customerReportService } from "../services/customerReportService";
import { exportActiveCoursesReportToExcel, exportCourseReportToExcel, exportCustomerReportToExcel } from "../utils/excelExport";


const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type FilterType = "date" | "range" | "month" | "year";

const ReportsDocumentPage: React.FC = () => {
  const [reportType, setReportType] = useState("customer");
  const [filterType, setFilterType] = useState<FilterType>("range");
  const [exportLoading, setExportLoading] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(7, "days"),
    dayjs(),
  ]);
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs());
  const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs>(dayjs());

  // สร้าง params สำหรับส่งไป API
  const reportParams: ReportParams = useMemo(() => {
    let startDate: string;
    let endDate: string;

    switch (filterType) {
      case "date":
        startDate = selectedDate.format("YYYY-MM-DD");
        endDate = selectedDate.format("YYYY-MM-DD");
        break;
      case "range":
        startDate = dateRange[0].format("YYYY-MM-DD");
        endDate = dateRange[1].format("YYYY-MM-DD");
        break;
      case "month":
        startDate = selectedMonth.startOf("month").format("YYYY-MM-DD");
        endDate = selectedMonth.endOf("month").format("YYYY-MM-DD");
        break;
      case "year":
        startDate = selectedYear.startOf("year").format("YYYY-MM-DD");
        endDate = selectedYear.endOf("year").format("YYYY-MM-DD");
        break;
      default:
        startDate = dateRange[0].format("YYYY-MM-DD");
        endDate = dateRange[1].format("YYYY-MM-DD");
    }

    return { startDate, endDate, filterType };
  }, [filterType, selectedDate, dateRange, selectedMonth, selectedYear]);

  // ดึงข้อมูลสำหรับ export
  const { data: customerData } = useQuery(
    ["customer-report-export", reportParams],
    () => customerReportService.getCustomerReport(reportParams),
    {
      enabled: reportType === "customer" && !!reportParams.startDate && !!reportParams.endDate,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: courseData } = useQuery(
    ["course-report-export", reportParams],
    () => customerReportService.getCourseReport(reportParams),
    {
      enabled: reportType === "courses" && !!reportParams.startDate && !!reportParams.endDate,
      staleTime: 5 * 60 * 1000,
    }
  );

  const { data: activeCoursesData } = useQuery(
    ["active-courses-report-export", reportParams],
    () => customerReportService.getActiveCoursesReport(reportParams),
    {
      enabled: reportType === "customer-courses" && !!reportParams.startDate && !!reportParams.endDate,
      staleTime: 5 * 60 * 1000,
    }
  );

  // ฟังก์ชัน Export
  const handleExportToExcel = async () => {
  setExportLoading(true);
  try {
    const dateRangeInfo = {
      startDate: reportParams.startDate,
      endDate: reportParams.endDate,
    };

    switch (reportType) {
      case "customer":
        // ✅ แก้ไขตรงนี้
        if (customerData?.data) {
          exportCustomerReportToExcel(customerData.data, dateRangeInfo);
          message.success("ສົ່ງອອກລາຍງານລູກຄ້າສຳເລັດ");
        } else {
          message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
        }
        break;
      case "courses":
        // ✅ แก้ไขตรงนี้
        if (courseData?.data) {
          exportCourseReportToExcel(courseData.data, dateRangeInfo);
          message.success("ສົ່ງອອກລາຍງານຄອສສຳເລັດ");
        } else {
          message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
        }
        break;
      case "customer-courses":
        // ✅ แก้ไขตรงนี້
        if (activeCoursesData?.data) {
          exportActiveCoursesReportToExcel(activeCoursesData.data, dateRangeInfo);
          message.success("ສົ່ງອອກລາຍງານຄອສກຳລັງໃຊ້ງານສຳເລັດ");
        } else {
          message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
        }
        break;
      default:
        message.error("ກະລຸນາເລືອກປະເພດລາຍງານ");
    }
  } catch (error) {
    console.error("Export error:", error);
    message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ");
  } finally {
    setExportLoading(false);
  }
};

  const renderDatePicker = () => {
    switch (filterType) {
      case "date":
        return (
          <DatePicker
            value={selectedDate}
            onChange={(date) => setSelectedDate(date || dayjs())}
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder="ເລືອກວັນທີ"
          />
        );
      case "range":
        return (
          <RangePicker
            value={dateRange}
            onChange={(dates) =>
              setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])
            }
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            placeholder={["ວັນທີເລີ່ມ", "ວັນທີ່ສິ້ນສຸດ"]}
          />
        );
      case "month":
        return (
          <DatePicker
            value={selectedMonth}
            onChange={(date) => setSelectedMonth(date || dayjs())}
            picker="month"
            style={{ width: "100%" }}
            format="MM/YYYY"
            placeholder="ເລືອກເດືອນ"
          />
        );
      case "year":
        return (
          <DatePicker
            value={selectedYear}
            onChange={(date) => setSelectedYear(date || dayjs())}
            picker="year"
            style={{ width: "100%" }}
            format="YYYY"
            placeholder="ເລືອກປີ"
          />
        );
      default:
        return null;
    }
  };

  const getSelectedDateInfo = () => {
    switch (filterType) {
      case "date":
        return `ວັນທີ: ${selectedDate.format("DD/MM/YYYY")}`;
      case "range":
        return `${dateRange[0].format("DD/MM/YYYY")} - ${dateRange[1].format("DD/MM/YYYY")}`;
      case "month":
        return `ເດືອນ: ${selectedMonth.format("MM/YYYY")}`;
      case "year":
        return `ປີ: ${selectedYear.format("YYYY")}`;
      default:
        return "";
    }
  };

  // Render รายงานตาม type
  const renderReport = () => {
    switch (reportType) {
      case "customer":
        return <CustomerReport params={reportParams} />;
      case "courses":
        return <CourseReport params={reportParams} />;
      case "customer-courses":
        return <ActiveCoursesReport params={reportParams} />;
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            📊 ລາຍງານ ລູກຄ້າ ແລະ ຄອສ
          </Title>
          <Text type="secondary">ວິເຄາະຂໍ້ມູນ ແລະ ປະສິດທິພາບທຸລະກິດ</Text>
        </Col>
      </Row>

      {/* Filter Section */}
      <Card
        bordered={false}
        style={{
          marginBottom: 24,
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ປະເພດລາຍງານ
              </Text>
              <Segmented
                value={reportType}
                onChange={setReportType}
                options={[
                  {
                    label: (
                      <Space>
                        <UserOutlined />
                        <span>ລູກຄ້າ</span>
                      </Space>
                    ),
                    value: "customer",
                  },
                  {
                    label: (
                      <Space>
                        <BarChartOutlined />
                        <span>ຄອສ</span>
                      </Space>
                    ),
                    value: "courses",
                  },
                  {
                    label: (
                      <Space>
                        <DollarOutlined />
                        <span>ຄອສໃຊ້ງານ</span>
                      </Space>
                    ),
                    value: "customer-courses",
                  },
                ]}
                block
                style={{ width: "100%" }}
              />
            </Space>
          </Col>

          <Col xs={24} sm={24} md={8}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ປະເພດການກອງ
              </Text>
              <Segmented
                value={filterType}
                onChange={(value) => setFilterType(value as FilterType)}
                options={[
                  {
                    label: (
                      <Space>
                        <CalendarOutlined />
                        <span>ວັນດຽວ</span>
                      </Space>
                    ),
                    value: "date",
                  },
                  {
                    label: (
                      <Space>
                        <CalendarOutlined />
                        <span>ຊ່ວງເວລາ</span>
                      </Space>
                    ),
                    value: "range",
                  },
                  {
                    label: (
                      <Space>
                        <CalendarOutlined />
                        <span>ເດືອນ</span>
                      </Space>
                    ),
                    value: "month",
                  },
                  {
                    label: (
                      <Space>
                        <CalendarOutlined />
                        <span>ປີ</span>
                      </Space>
                    ),
                    value: "year",
                  },
                ]}
                block
                style={{ width: "100%" }}
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {filterType === "date" && "ເລືອກວັນທີ"}
                {filterType === "range" && "ເລືອກຊ່ວງເວລາ"}
                {filterType === "month" && "ເລືອກເດືອນ"}
                {filterType === "year" && "ເລືອກປີ"}
              </Text>
              {renderDatePicker()}
            </Space>
          </Col>
        </Row>

        <Row style={{ marginTop: 16 }} gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Card
              size="small"
              style={{ background: "#f0f5ff", border: "1px solid #d6e4ff" }}
            >
              <Space>
                <CalendarOutlined style={{ color: "#1890ff" }} />
                <Text strong style={{ color: "#1890ff" }}>
                  {getSelectedDateInfo()}
                </Text>
              </Space>
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Space style={{ width: "100%" }}>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportToExcel}
                loading={exportLoading}
                style={{
                  background:
                    "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                  border: "none",
                }}
              >
                ສົ່ງອອກ Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Report Content */}
      {renderReport()}
    </div>
  );
};

export default ReportsDocumentPage;